import React from 'react';
import { 
  FileText, 
  ArrowUpRight, 
  ArrowDownCircle, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Hash
} from 'lucide-react';

const TransactionHistory = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm mt-12">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-gray-50 rounded-2xl">
            <Hash className="h-8 w-8 text-gray-300" />
          </div>
        </div>
        <h3 className="text-lg font-black text-gray-900">No Transactions Yet</h3>
        <p className="text-gray-500 text-sm mt-1">Your billing history will appear here once you make your first purchase.</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="text-[10px] font-black uppercase tracking-tighter">Completed</span>
          </div>
        );
      case 'pending':
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-full">
            <Clock className="h-3.5 w-3.5" />
            <span className="text-[10px] font-black uppercase tracking-tighter">Pending</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-red-50 text-red-700 rounded-full">
            <XCircle className="h-3.5 w-3.5" />
            <span className="text-[10px] font-black uppercase tracking-tighter">Failed</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 text-gray-700 rounded-full">
            <span className="text-[10px] font-black uppercase tracking-tighter">{status}</span>
          </div>
        );
    }
  };

  const getOrderTypeInfo = (type) => {
    if (type === 'plan_purchase') {
      return {
        label: 'Plan Purchase',
        icon: ArrowUpRight,
        color: 'text-primary-600',
        bg: 'bg-primary-50'
      };
    }
    return {
      label: 'Top Up',
      icon: ArrowDownCircle,
      color: 'text-secondary-600',
      bg: 'bg-secondary-50'
    };
  };

  return (
    <div className="mt-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black text-gray-900">Transaction History</h2>
        <button className="flex items-center gap-2 text-xs font-bold text-primary-600 hover:text-primary-700">
          <FileText className="h-4 w-4" />
          Download All
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</th>
                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Messages</th>
                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {history.map((tx, idx) => {
                const typeInfo = getOrderTypeInfo(tx.orderType);
                const Icon = typeInfo.icon;
                
                return (
                  <tr key={idx} className="hover:bg-gray-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${typeInfo.bg}`}>
                          <Icon className={`h-4 w-4 ${typeInfo.color}`} />
                        </div>
                        <span className="text-sm font-bold text-gray-900">{typeInfo.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-bold text-gray-500">
                        {new Date(tx.createdAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-black text-gray-900">₹{tx.amount.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-gray-600">{tx.messageCount.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(tx.status)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
